import _ from "lodash";
/**--external-- */
import React, { useEffect, useRef } from "react";
import { graphql, compose } from "lib/apolloClient";
import { connect } from "react-redux";
import { FolderFilled } from "@toddle-edu/ds-icons";

/**--internal-- */
import {
  ELEMENT_TYPE_MYP_OBJECTIVES,
  ELEMENT_TYPE_PERSONAL_OBJECTIVES,
  ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
} from "Constants/stringConstants";
import Loading from "UIComponents/Loading";
import { getEntitySubType } from "Utils";
import { getProjectObjectivesLabelMapping } from "ProgressReportCommon/utils";
import { getDefaultNameSortPreference } from "modules/CommonSelectors";
import { colors } from "Constants";
import { KeyboardNavigableList } from "UIComponents/KeyboardNavigableList";

/**--relative-- */
import {
  getFilterOptionParam,
  getFilterOptions,
  getFilterDisabilityStatusConfig,
  getFilterParam,
  getValidFilterValuesStatus,
  getPortfolioFolderFilterTooltip,
} from "../../../../modules/Utils";
import classes from "./FilterOptions.scss";
import {
  getFilterOptionsQuery,
  getSubjectsQuery,
} from "../../../../modules/Queries";
import { getFilterOptionsFromCache } from "../../../../modules/GraphqlHelpers";

const getFilteredOptions = ({ searchText = "", options = [] }) => {
  const _searchText = _.lowerCase(_.trim(searchText));

  if (!_.isEmpty(_searchText)) {
    const newOptions = [];

    _.forEach(options, option => {
      const childOptions = _.get(option, "options", []);
      const label = _.lowerCase(_.get(option, "label", ""));

      // * grouped options
      if (!_.isEmpty(childOptions)) {
        // * return the entire group if the group label matches with search text
        if (_.includes(label, _searchText)) {
          newOptions.push({ ...option });
        }
        // * return only those child options that match with search text
        else {
          const filteredChildOptions = _.filter(
            _.map(childOptions, childOption => {
              const childLabel = _.lowerCase(_.get(childOption, "label"));
              if (_.includes(childLabel, _searchText)) {
                return { ...childOption };
              } else {
                const grandChildOptions = _.get(childOption, "options", []);
                if (!_.isEmpty(grandChildOptions)) {
                  const filteredGrandChildOptions = _.filter(
                    grandChildOptions,
                    grandChildOption => {
                      const grandChildLabel = _.lowerCase(
                        _.get(grandChildOption, "label")
                      );
                      return _.includes(grandChildLabel, _searchText);
                    }
                  );
                  if (!_.isEmpty(filteredGrandChildOptions)) {
                    return {
                      ...childOption,
                      options: filteredGrandChildOptions,
                    };
                  }
                }
              }
            }),
            _.identity
          );

          if (!_.isEmpty(filteredChildOptions)) {
            newOptions.push({ ...option, options: filteredChildOptions });
          }
        }
      }
      // * single options
      else {
        _.includes(label, _searchText) && newOptions.push(option);
      }
    });

    return newOptions;
  } else {
    return options;
  }
};

const getDisabledStateInfoElement = ({
  label,
  filterDisabilityStatusConfig,
  t,
}) => {
  const { stateCode, parentFilterLabel } = filterDisabilityStatusConfig;

  switch (stateCode) {
    case "LOADING":
      return <Loading />;
    case "NO_OPTIONS":
      return t("common:no_with_label", {
        label: t("common:option_plural"),
      });
    case "PARENT_FILTER_MULTI_VALUE":
      return t("snp:select_one_parent_option", {
        parent: parentFilterLabel,
        label,
      });
    default:
      return null;
  }
};

const getTransformedOptions = (options, parentKey) => {
  return _.map(options, option => {
    const childOptions = _.get(option, "options");
    if (_.isEmpty(childOptions)) {
      const optionValue = _.get(option, "value");
      const updatedOptionValue = _.isEmpty(parentKey)
        ? optionValue
        : `${parentKey}.${optionValue}`;
      return {
        ...option,
        value: updatedOptionValue,
      };
    } else {
      const optionValue = _.get(option, "key");
      const updatedParentKey = _.isEmpty(parentKey)
        ? optionValue
        : `${parentKey}.${optionValue}`;
      return {
        ...option,
        options: getTransformedOptions(childOptions, updatedParentKey),
      };
    }
  });
};

const getAllValues = options => {
  return _.concat(
    ..._.map(options, ({ options: childOptions, value }) => {
      if (childOptions) {
        return getAllValues(childOptions);
      }
      return [value];
    })
  );
};

const FilterOptions = props => {
  const {
    getAllCheckboxComp,
    getSearchBar,
    entityType,
    isCheckList,
    searchText,
    options: originalOptions,
    getCheckboxComp,
    levels,
    isLoading,
    parentFiltersData,
    dependentFilterParents,
    label,
    t,
    updateInputField,
    values,
    projectNodeDetails,
    updateFilterOptions,
  } = props;
  const totalOptions = _.size(originalOptions);
  const filterDisabilityStatusConfig = getFilterDisabilityStatusConfig({
    parentFiltersData,
    config: dependentFilterParents,
    totalOptions,
    isLoading,
    levels,
  });

  const checkboxItemsRef = useRef(null);

  const options = getTransformedOptions(originalOptions);

  /**update filter options when old data from cache is available,
   * latest data is available.
   * By updating options this way, user can see options early,
   * if they are available in cache.  */
  useEffect(() => {
    if (!isLoading) {
      const { areValuesUpdated, updatedValues } = getValidFilterValuesStatus({
        options,
        values,
        entityType,
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      });

      if (areValuesUpdated) {
        updateInputField({ values: updatedValues, key: entityType });
      }
    }

    updateFilterOptions({
      filterOptions: options,
      filter: entityType,
    });
  }, [isLoading]);

  const filteredSelectedOptions = _.isEmpty(searchText)
    ? options
    : getFilteredOptions({
        searchText,
        options,
      });

  const allValues = getAllValues(filteredSelectedOptions);

  return !filterDisabilityStatusConfig.isDisabled ? (
    <>
      {entityType !== "EVIDENCE_FOLDER"
        ? getSearchBar({
            placeholder: `${t("common:search_label", { label })}...`,
          })
        : null}
      {isCheckList && entityType !== ELEMENT_TYPE_MYP_OBJECTIVES
        ? getAllCheckboxComp({
            totalOptionsCount: _.size(allValues),
            allValues,
            ...(entityType !== "EVIDENCE_FOLDER"
              ? {}
              : {
                  allOptionLabel: t("journal:all_folders"),
                  allOptionPrefix: (
                    <FolderFilled style={{ color: colors.blue400 }} />
                  ),
                }),
          })
        : null}

      <KeyboardNavigableList listContainerRef={checkboxItemsRef}>
        <div className={classes.checkboxItemsContainer} ref={checkboxItemsRef}>
          {_.map(filteredSelectedOptions, (filterOption, index) => {
            const isLast = _.size(filteredSelectedOptions) - 1 === index;

            return getCheckboxComp({
              entityType,
              filterOption,
              isLast,
            });
          })}
        </div>
      </KeyboardNavigableList>
    </>
  ) : (
    <div className={classes.disabledState}>
      {entityType === "EVIDENCE_FOLDER"
        ? getPortfolioFolderFilterTooltip({
            label,
            filterDisabilityStatusConfig,
            t,
          })
        : getDisabledStateInfoElement({
            label,
            filterDisabilityStatusConfig,
            t,
          })}
    </div>
  );
};

const mapStateToProps = (state, ownProps) => {
  const organizationId = _.get(state, "login.userInfo.org_id", "");

  const {
    entityType,
    parentFiltersData,
    curriculumProgramId,
    parentFilterObj,
    dependentFilterParents,
    curriculumType,
    projectNodeDetails,
    activeTabClassesFilterOptions,
    filterByLearningCourse,
  } = ownProps;

  const dependentFilters = _.get(parentFilterObj, "dependentFilters", []);

  const currentParentFilters = _.map(
    dependentFilterParents,
    ({ parent }) => parent
  );

  const commonFilters = _.intersection(dependentFilters, currentParentFilters);

  const optionParam = getFilterOptionParam({
    entityType,
    parentFiltersData,
    curriculumProgramId,
    curriculumType,
    activeTabClassesFilterOptions,
    filterByLearningCourse,
  });

  const filterParam = getFilterParam({ entityType, parentFiltersData });

  let filterParamWithEntitySubType = {};

  if (
    _.includes(
      [
        ELEMENT_TYPE_MYP_OBJECTIVES,
        ELEMENT_TYPE_PERSONAL_OBJECTIVES,
        ELEMENT_TYPE_COMMUNITY_OBJECTIVES,
      ],
      entityType
    )
  ) {
    filterParamWithEntitySubType = getFilterParam({
      entityType,
      parentFiltersData,
      entitySubType: getEntitySubType({
        projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
      }),
    });
  }

  const subjectsQueryVariables = {
    organizationId,
    filters: {
      curriculumProgramIds: [curriculumProgramId],
      archivalState: "ALL",
    },
  };

  return {
    id: organizationId,
    optionParam,
    shouldSkipQuery: !_.isEmpty(commonFilters),
    filterOptions: [],
    filterParam,
    isLoading: true,
    isData: false,
    filterParamWithEntitySubType,
    defaultNameSortPreference: getDefaultNameSortPreference(state),
    subjectsQueryVariables,
  };
};

export default compose(
  connect(mapStateToProps),
  graphql(getSubjectsQuery, {
    name: "getSubjects",
    skip: ({ entityType }) => entityType !== ELEMENT_TYPE_MYP_OBJECTIVES,
    options: ({ subjectsQueryVariables }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: subjectsQueryVariables,
      };
    },
    props: ({ getSubjects, ownProps: { isData, isLoading } }) => {
      const data = _.get(getSubjects, "node", {});
      const networkStatus = _.get(getSubjects, "networkStatus");

      return {
        allSubjects: _.get(data, "subjects", []),
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
      };
    },
  }),
  graphql(getFilterOptionsQuery, {
    name: "getFilterOptionsQuery",
    alias: "getFilterOptionsQuery",
    skip: ({ shouldSkipQuery }) => shouldSkipQuery,
    options: ({
      baseSetType,
      entityType,
      id,
      optionParam,
      isProjectNode,
      filterParam,
      filterParamWithEntitySubType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          entityType: baseSetType ? baseSetType : entityType,
          id,
          options: optionParam,
          filters: isProjectNode ? filterParamWithEntitySubType : filterParam,
        },
      };
    },
    props: ({
      getFilterOptionsQuery: { variables, networkStatus },
      ownProps: {
        entityType,
        levels,
        attributes,
        defaultNameSortPreference,
        isProjectNode,
        projectNodeDetails,
        allSubjects,
      },
    }) => {
      const data = getFilterOptionsFromCache(variables);

      let updatedOptions = [];

      const isCachedDataAvailable = !_.isEmpty(data);

      if (isCachedDataAvailable) {
        updatedOptions = getFilterOptions({
          data,
          levels,
          attributes,
          entityType,
          defaultNameSortPreference,
          projectNodeUID: _.get(projectNodeDetails, "projectNodeUID", ""),
          allSubjects,
        });
      }

      if (isProjectNode && entityType === ELEMENT_TYPE_MYP_OBJECTIVES) {
        const projectNodeUID = _.get(projectNodeDetails, "projectNodeUID", "");
        updatedOptions =
          projectNodeUID === "PP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "PERSONAL",
                }),
              }))
            : projectNodeUID === "CP_DETAILS"
            ? _.map(updatedOptions, (option, index) => ({
                ...option,
                label: getProjectObjectivesLabelMapping({
                  index,
                  projectType: "COMMUNITY",
                }),
              }))
            : updatedOptions;
      }

      return {
        isData: !_.isEmpty(data),
        isLoading: _.includes([1, 2], networkStatus),
        options: updatedOptions,
      };
    },
  }),
  graphql(getFilterOptionsQuery, {
    name: "getFilterOptionsQuery",
    alias: "getFilterOptionsQuery(Interdisciplinary Objectives)",
    skip: ({ entityType, isProjectNode }) => {
      return entityType != ELEMENT_TYPE_MYP_OBJECTIVES || isProjectNode;
    },
    options: ({
      baseSetType,
      entityType,
      id,
      optionParam,
      filterParamWithEntitySubType,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: {
          entityType: baseSetType ? baseSetType : entityType,
          id,
          options: optionParam,
          filters: filterParamWithEntitySubType,
        },
      };
    },
    props: ({ getFilterOptionsQuery, ownProps }) => {
      const { networkStatus } = getFilterOptionsQuery;

      const {
        baseSetType,
        entityType,
        id,
        optionParam,
        levels,
        attributes,
        isLoading,
        isData,
        options,
        filterParamWithEntitySubType,
      } = ownProps;

      const data = getFilterOptionsFromCache({
        entityType: baseSetType ? baseSetType : entityType,
        options: optionParam,
        id,
        filters: filterParamWithEntitySubType,
      });

      let updatedOptions = [];

      const isCachedDataAvailable = !_.isEmpty(data);

      if (isCachedDataAvailable) {
        updatedOptions = getFilterOptions({
          data,
          levels,
          attributes,
          entityType,
        });
      }

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading: _.includes([1, 2], networkStatus) || isLoading,
        options: _.isEmpty(options)
          ? updatedOptions
          : [...options, ...updatedOptions],
      };
    },
  })
)(FilterOptions);
